import { FlexRow } from '@/components/common/Flex';
import {
    EyebrowHeader,
    Header,
    Paragraph,
    Text,
} from '@/components/common/Typography';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { showConfetti } from '@/util/confetti';
import { ListItem, Spacer, UnorderedList } from '@chakra-ui/react';
import { ExternalFinancialEntity } from '@common/financialEntities';
import {
    useAccountLinkingConfirmationFlow,
    useAccountsForLinkingConfirmation,
} from '@common/react/accountLinking';
import { usePageContext } from '@common/react/pageMode';
import { ComponentProps } from 'react';
import { Button } from '../common/Button';
import ButtonWithIcon from '../common/ButtonWithIcon';
import { FlexCol } from '../common/Flex';
import { ArrowLeft } from '../common/icons';
import Modal, { ModalProps } from '../common/modals/Modal';
import ModalManager, { useModal } from '../ModalManager';
import AccountLinkingConfirmationInitialSubstep from './AccountLinkingConfirmationInitialSubstep';
import AccountLinkingConfirmationJointOwnerSubstep from './AccountLinkingConfirmationJointOwnerSubstep';
import AccountLockup from './AccountLockup';
import { ACCOUNT_LINKING_MODAL_CONTENT_HEIGHT } from './helpers';

export interface AccountLinkingConfirmationModalProps {
    financialEntityIds: string[];
    nextButtonText?: string;
    startWithJointOwnerSubstep?: boolean;
    closeOnEsc?: boolean;
    showCloseButton?: boolean;
    closeOnOverlayClick?: boolean;
    onComplete?: VoidFunction;
}

export const showAccountLinkingConfirmationModal = (
    props: AccountLinkingConfirmationModalProps
) => ModalManager.show(AccountLinkingConfirmationModal, props);

const AccountLinkingConfirmationModal =
    ModalManager.create<AccountLinkingConfirmationModalProps>(
        ({
            financialEntityIds,
            nextButtonText,
            startWithJointOwnerSubstep,
            closeOnEsc,
            showCloseButton,
            closeOnOverlayClick = false,
            onComplete,
        }) => {
            useTrackModalShown('Account Linking Confirmation');

            const modal = useModal();
            const accounts = useAccountsForLinkingConfirmation({
                financialEntityIds,
            });
            if (!accounts) return null;

            const modalProps: ComponentProps<typeof Modal> = {
                size: 'lg',
                isCentered: true,
                isOpen: modal.visible,
                closeOnOverlayClick,
                closeOnEsc,
                showCloseButton,
                onClose: () => modal.remove(),
            };

            return accounts.length === 0 ? (
                <AccountLinkingConfirmationModalWithoutAccounts
                    modalProps={modalProps}
                    onComplete={() => {
                        modal.remove();
                        onComplete?.();
                    }}
                    nextButtonText={nextButtonText}
                />
            ) : (
                <AccountLinkingConfirmationModalWithAccounts
                    accounts={accounts}
                    modalProps={modalProps}
                    onComplete={() => {
                        modal.remove();
                        showConfetti();
                        onComplete?.();
                    }}
                    nextButtonText={nextButtonText}
                    startWithJointOwnerSubstep={startWithJointOwnerSubstep}
                />
            );
        }
    );

const AccountLinkingConfirmationModalWithoutAccounts = ({
    modalProps,
    onComplete,
    nextButtonText,
}: {
    modalProps: ModalProps;
    onComplete: VoidFunction;
    nextButtonText?: string;
}) => {
    return (
        <Modal
            {...modalProps}
            renderContent={() => (
                <FlexCol h={ACCOUNT_LINKING_MODAL_CONTENT_HEIGHT}>
                    <Header mb={8}>No accounts connected</Header>
                    <Text>
                        No accounts could be connected from that institution.
                        Please try again with a different institution.
                    </Text>
                </FlexCol>
            )}
            renderFooter={() => (
                <FlexRow w="full" justifyContent="flex-end" p={5}>
                    <Button
                        onClick={onComplete}
                        onClickEvent="Account Confirmation No Accounts Button Clicked"
                    >
                        {nextButtonText ?? 'Done'}
                    </Button>
                </FlexRow>
            )}
        />
    );
};

const AccountLinkingConfirmationModalWithAccounts = ({
    accounts,
    modalProps,
    onComplete,
    nextButtonText,
    startWithJointOwnerSubstep,
}: {
    accounts: ExternalFinancialEntity[];
    privateFinancialEntityIds?: string[];
    modalProps: ModalProps;
    onComplete: VoidFunction;
    nextButtonText?: string;
    startWithJointOwnerSubstep?: boolean;
}) => {
    const { pageMode, showPopover } = usePageContext();

    const {
        currentAccount,
        substep,
        ownershipType,
        setOwnershipType,
        feCombinedTypeOptions,
        feCombinedType,
        setFECombinedType,
        isPrivate,
        setIsPrivate,
        isPartnerJointOwner,
        setIsPartnerJointOwner,
        eyebrowHeaderStr,
        headerStr,
        primaryButtonText,
        isPrimaryButtonVisible,
        isPrimaryButtonDisabled,
        isSubmitting,
        onNext,
        onBack,
        onClickReadMore,
        privateAccountsCount,
        jointAccountsCount,
    } = useAccountLinkingConfirmationFlow({
        accounts,
        nextButtonText,
        startWithJointOwnerSubstep,
        onComplete: () => {
            if (pageMode === 'PUBLIC' && privateAccountsCount > 0) {
                showPopover(privateAccountsCount, 'private');
            } else if (pageMode === 'MINE' && jointAccountsCount > 0) {
                showPopover(jointAccountsCount, 'joint');
            }

            onComplete();
        },
    });

    return (
        <Modal
            showCloseButton={false}
            {...modalProps}
            renderContent={() =>
                substep === 'INITIAL' || substep === 'JOINT_OWNER' ? (
                    <FlexCol h={ACCOUNT_LINKING_MODAL_CONTENT_HEIGHT}>
                        {eyebrowHeaderStr && (
                            <EyebrowHeader>{eyebrowHeaderStr}</EyebrowHeader>
                        )}
                        <Header mb={6}>{headerStr}</Header>
                        <FlexRow mb={6} px={2}>
                            <AccountLockup
                                account={currentAccount}
                                showBalance
                                showLoadingIndicator={false}
                                showLastUpdated={false}
                            />
                        </FlexRow>
                        {substep === 'INITIAL' ? (
                            <AccountLinkingConfirmationInitialSubstep
                                feType={currentAccount.type}
                                ownershipType={ownershipType}
                                setOwnershipType={setOwnershipType}
                                feCombinedTypeOptions={feCombinedTypeOptions}
                                feCombinedType={feCombinedType}
                                setFeCombinedType={setFECombinedType}
                                isPrivate={isPrivate}
                                setIsPrivate={setIsPrivate}
                                feJointOwnerPartnershipId={
                                    currentAccount.jointOwnerPartnershipId
                                }
                            />
                        ) : (
                            <AccountLinkingConfirmationJointOwnerSubstep
                                isPartnerJointOwner={isPartnerJointOwner}
                                setIsPartnerJointOwner={setIsPartnerJointOwner}
                                onClickReadMore={onClickReadMore}
                            />
                        )}
                    </FlexCol>
                ) : (
                    // substep === 'READ_MORE'
                    <FlexCol h={ACCOUNT_LINKING_MODAL_CONTENT_HEIGHT}>
                        <ReadMoreContent />
                    </FlexCol>
                )
            }
            renderFooter={() => (
                <FlexRow w="full" p={6}>
                    {onBack && (
                        <ButtonWithIcon
                            variant="unstyled"
                            iconLeft={<ArrowLeft size="sm" />}
                            color="gray.500"
                            onClick={onBack}
                            onClickEvent="Joint Confirmation Back Button Clicked"
                            ml={6}
                        >
                            Back
                        </ButtonWithIcon>
                    )}
                    <Spacer />
                    {isPrimaryButtonVisible && (
                        <Button
                            onClick={onNext}
                            onClickEvent="Joint Confirmation Next Button Clicked"
                            onClickEventParams={{ label: primaryButtonText }}
                            isDisabled={isPrimaryButtonDisabled}
                            isLoading={isSubmitting}
                        >
                            {primaryButtonText}
                        </Button>
                    )}
                </FlexRow>
            )}
        />
    );
};

// N.B. Keep this in sync with
// mobile/components/accountLinking/AccountLinkingConfirmationWithAccounts.tsx
const ReadMoreContent = () => {
    return (
        <>
            <Header mb={8}>About legal account owners</Header>
            <Paragraph mb={3}>
                When you confirm that an account is joint and your partner is a
                legal account owner, your partner can:
            </Paragraph>
            <UnorderedList mb={3}>
                <ListItem>
                    <Paragraph>
                        Move money into Plenty from your joint account
                    </Paragraph>
                </ListItem>
                <ListItem>
                    <Paragraph>
                        Withdraw money from Plenty into your joint account
                    </Paragraph>
                </ListItem>
            </UnorderedList>
            <Paragraph>
                Your partner will be able to see your choice in their dashboard.
                If you confirm that your partner is NOT a legal owner but you
                would like to share visibility of the account, your partner will
                be able to view the account but cannot use the account to
                deposit from or withdraw funds to. Visibility of the account
                includes visibility of the current balance and past transaction
                details.
            </Paragraph>
        </>
    );
};

export default AccountLinkingConfirmationModal;
